import { offsetLimitPagination } from "@apollo/client/utilities";

export function cacheMerge(existing, incoming, { args: { offset = null } }) {
  // if offset is not provided at all and incoming is empty then just set to empty array
  if (offset == null && incoming.length == 0) return incoming;
  const merged = existing ? existing.slice(0) : [];
  for (let i = 0; i < incoming.length; ++i) {
    merged[offset + i] = incoming[i];
  }
  return merged;
}

export function registrationsPagination() {
  return {
    read(existing, { args: { offset, limit } }) {
      return existing;
    },
    // The keyArgs list and merge function are the same as above.
    keyArgs: [
      "ids",
      "eventId",
      "includeUnregistered",
      "registrantOrRegisteredPersonId",
      "eventStartDateFrom",
      "horseId",
      "registrantPersonId",

      "competitionIds",
      "keyword",
      "withEntriesInCombinedStatuses",
      "paradressageGrades",

      "loadingBatchName",
      "registrationPersonFilter",
      "registrationHorseFilter",
      "registrationEventProductFilter",
      "entryFilter"
    ],
    merge(existing, incoming, { args: { offset = null } }) {
      // if offset is not provided at all and incoming is empty then just set to empty array
      if (offset == null && incoming.length == 0) return incoming;
      const merged = existing ? existing.slice(0) : [];
      for (let i = 0; i < incoming.length; ++i) {
        merged[offset + i] = incoming[i];
      }
      return merged;
    }
  };
}

export default {
  payables: {
    ...offsetLimitPagination([
      "createdAtFrom",
      "createdAtUntil",
      "productIds",
      "recipientOrganisationIds",
      "statuses",
      "keyword",
      "eventIds",
      "personIds",
      "horseIds",
      "organisationIds",
      "onlyInvoiced",
      "invoiceNumber",
      "paymentReferenceCode",
      "firstPaymentConfirmedAtFrom",
      "firstPaymentConfirmedAtUntil"
    ])
  },
  eventCashierPeople: {
    ...offsetLimitPagination([
      "eventId",
      "involvedPersonIds",
      "involvedHorseIds",
      "hasNotPaidProducts",
      "hasNotPaidEntries",
      "allProductsPaid",
      "allEntriesPaid",
      "hasUsedOrPaidReturns",
      "hasAvailableReturns",
      "hasPrizesNotPaidOut",
      "allPrizesPaidOut",
      "hasPrizesAllocated",
      "hasMissingPrizePaymentDetails"
    ])
  },
  groups: {
    ...offsetLimitPagination([
      "includeInactive",
      "keyword",
      "active",
      "participationTypes",
      "sortBy"
    ])
  },
  contacts: {
    ...offsetLimitPagination(["namedFilters"])
  },
  personDuplicates: {
    ...offsetLimitPagination([
      "createdAtFrom",
      "createdAtUntil",
      "keyword",
      "regionIds",
      "includeIgnored",
      "sortBy",
      "sortDesc"
    ])
  },
  personDebtBalances: {
    ...offsetLimitPagination([
      "organisationIds",
      "updatedAtFrom",
      "updatedAtUntil",
      "dataSourceTypes",
      "keyword",
      "sortBy",
      "sortDesc"
    ])
  },
  personCertificates: {
    ...offsetLimitPagination([
      "certificateDefinitionIds",
      "awardingBodyOrganisationIds",
      "certificateGroupIds",
      "issueDateFrom",
      "issueDateUntil",
      "keyword",
      "personIds",
      "sortBy",
      "sortDesc"
    ])
  },
  personExams: {
    ...offsetLimitPagination([
      "examGroupIds",
      "examDateFrom",
      "examDateUntil",
      "disciplineIds",
      "examDefinitionIds",
      "keyword",
      "resolutions",
      "personIds",
      "sortBy",
      "sortDesc"
    ])
  },
  invoices: {
    ...offsetLimitPagination([
      "issueDateFrom",
      "issueDateUntil",
      "remittanceCode",
      "productIds",
      "statuses",
      "keyword",
      "paymentDueDateFrom",
      "paymentDueDateUntil",
      "paidDateFrom",
      "paidDateUntil",
      "createdAtFrom",
      "createdAtUntil",
      "eventIds",
      "personIds",
      "horseIds",
      "invoiceNumber",
      "isCredited",
      "isOverdue",
      "paymentReferenceCode",
      "buyerContactCountryIds",
      "buyerContactType",
      "buyerContactHasVatCode",
      "issueAutomatically",
      "sortBy",
      "sortDesc"
    ])
  },
  registrations: registrationsPagination(),
  events: {
    ...offsetLimitPagination([
      "keyword",
      "disciplineIds",
      "competitionLevelIds",
      "regionIds",
      "eventIds",
      "organiserIds",
      "place",
      "statuses",
      "endDateFrom",
      "registrationEndDateUntil",
      "cupIds",
      "walletStatuses",
      "useEquipe",
      "startDateFrom",
      "startDateUntil",
      "hasFinancialActivitiesWithPersonIds",
      "noncompetition",
      "eventCategoryIds",
      "profQualificationIds",
      "countryIds",
      "isHighlighted",
      "hasTeamCompetitions",
      "hasChampionshipCompetitions",
      "hasEntries",
      "hasAcceptedEntries",
      "hasCandidacyEntries",
      "closingSoon",
      "recentlyOpened",
      "sortBy",
      "sortDesc",
      "noPeopleAssignedInRoleIds",
      "personRoleFilter",
      "organiserPersonRoleFilter",
      "hasPendingPrizeMoneyPayouts",
      "hasEntriesWithPrizeMoneyButWithoutPrizePayout",
      "useOnlinePaymentsForPrizeMoney",

      "hasOpenShoppingBaskets",
      "limit" // because normal calendar has 45 and admin calendar has 15. It breaks @client directive
    ])
  },
  organisations: {
    ...offsetLimitPagination([
      "hasActivePersonOrganisationFunctions",
      "keyword",
      "paymentProcessors",
      "walletStatuses",
      "organisationIds",
      "regionIds",
      "membershipStatuses",
      "membershipTypes",
      "hasVatObligation",
      "isRidingSchool"
    ])
  },
  payments: {
    ...offsetLimitPagination([
      "referenceCode",
      "statuses",
      "recipientOrganisationIds",
      "recipientEventIds",
      "fromPersonIds",
      "createdAtFrom",
      "createdAtUntil",
      "horseIds",
      "productIds",
      "keyword",
      "processors"
    ])
  },
  payouts: {
    ...offsetLimitPagination([
      "referenceCode",
      "statuses",
      "reasons",
      "fromOrganisationIds",
      "fromEventIds",
      "recipientPersonIds",
      "createdAtFrom",
      "createdAtUntil",
      "fromEventIds",
      "keyword"
    ])
  },
  personApplications: {
    ...offsetLimitPagination([
      "productCodenames",
      "statuses",
      "paymentStatuses"
    ])
  },
  horseApplications: {
    ...offsetLimitPagination([
      "productCodenames",
      "statuses",
      "paymentStatuses",
      "sortBy",
      "sortDesc",
      "createdAtFrom",
      "createdAtUntil",
      "relatedWithPersonIds",
      "horseIds"
    ])
  },
  auditEvents: {
    ...offsetLimitPagination([
      "keyword",
      "names",
      "actionTypes",
      "results",
      "actorPersonIds",
      "personIds",
      "relatedWithPersonIds",
      "horseIds",
      "organisationIds",
      "eventIds",
      "createdAtFrom",
      "createdAtUntil"
    ])
  },
  taxOfficeReports: {
    ...offsetLimitPagination([
      "keyword",
      "disciplineIds",
      "competitionLevelIds",
      "regionIds",
      "eventIds",
      "organiserIds",
      "place",
      "statuses",
      "endDateFrom",
      "registrationEndDateUntil",
      "isHighlighted",
      "cupIds",
      "walletStatuses",
      "useEquipe"
    ])
  },
  sanctions: {
    ...offsetLimitPagination([
      "relatedWithPersonIds",
      "relatedWithHorseIds",
      "relatedWithEventIds",
      "approved",
      "discarded",
      "sanctionedEntities",
      "sortBy",
      "sortDesc"
    ])
  },
  extResApplications: {
    ...offsetLimitPagination([
      "statuses",
      "disciplineIds",
      "applicantPersonIds",
      "athleteId",
      "horseId",
      "submittedAtFrom",
      "submittedAtUntil"
    ])
  },
  walletActions: {
    ...offsetLimitPagination(["eventIds"])
  },
  results: {
    ...offsetLimitPagination([
      "personId",
      "horseId",
      "keyword",
      "competitionId",
      "fromLastImport",
      "disciplineIds",
      "competitionLevelIds",
      "organiserIds",
      "eventPlace",
      "difficultyLevelIds",
      "competitionStartDateFrom",
      "competitionStartDateUntil",
      "eventIsHighlighted",
      "cupIds",
      "eventcodeIds",
      "isPlaced",
      "hasPoints",
      "inTeamCompetition",
      "satisfiesQualification"
    ])
  },
  horses: {
    ...offsetLimitPagination([
      "keyword",
      "horseIds",
      "currentSeasonLicenceValid",
      "includeInactive",
      "onlyInactive",
      "hasIdentifiersFrom",
      "breedIds",
      "studbookIds",
      "birthCountryIds",
      "birthYear",
      "ownerName",
      "horseType",
      "ponyType",
      "hasIcelandicRegistryNumber",
      "hasWesternRegistryNumber",
      "hasFeiid",
      "feiPassportExpiresThisYear",
      "feiLicenceValidForDisciplineIds",
      "personRoleFilter",
      "sortBy",
      "sortDesc"
    ])
  },
  paymentTransactions: {
    ...offsetLimitPagination([
      "referenceCode",
      "statuses",
      "createdAtFrom",
      "createdAtUntil"
    ])
  },
  organisationCurrentMembers: {
    ...offsetLimitPagination([
      "organisationId",
      "personIds",
      "haveAnyOfCurrentLicenceCategoryIds",
      "haveSeasonLicenceForCurrentYear",
      "currentlyRepresentingGivenOrganisation",
      "membershipInAnyOfCategories",
      "seasonLicenceForOneOfYears"
    ])
  },
  people: {
    ...offsetLimitPagination([
      "personOrganisationFunctionFilter",
      "keyword",
      "licenceCategoryIds",
      "profQualificationIds",
      "profQualificationGroupIds",
      "profQualificationDisciplineIds",
      "personIds",
      "clubIds",
      "countryIds",
      "currentSeasonLicenceValid",
      "hasActiveIdentifiers",
      "hasIdentifiersFrom",
      "roleIds",
      "roleInRegionIds",
      "roleInDisciplineIds",
      "representingRegionIds",
      "seasonLicenceForOneOfYears",

      "emailIsPublic",
      "phoneIsPublic",
      "personProfQualificationFilter",

      "hasFeiid",
      "feiLicenceValidForDisciplineIds",

      "applicableForRoleIds",
      "applicableForRoleIdsCheckDate"
    ])
  },
  peopleSearch: {
    ...offsetLimitPagination([
      "clubIds",
      "countryIds",
      "currentSeasonLicenceValid",
      "hasActiveIdentifiers",
      "hasIdentifiersFrom",
      "keyword",
      "licenceCategoryId",
      "licenceCategoryIds",
      "profQualificationDisciplineIds",
      "profQualificationGroupIds",
      "profQualificationId",
      "profQualificationIds",
      "representingRegionIds",
      "roleIds",
      "roleInDisciplineIds",
      "roleInRegionIds",
      "sortBy",
      "sortDesc",
      "personIds",
      "seasonLicenceForOneOfYears",
      "personOrganisationFunctionFilter",

      "hasFeiid",
      "feiLicenceValidForDisciplineIds"
    ])
  },
  competingDifficultyLevels: {
    ...offsetLimitPagination([
      "validCurrently",
      "keyword",
      "competingDifficultyLevelGroupIds",
      "disciplineIds",
      "forParticipantsInEventId"
    ])
  }
};
